import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';
import { Link } from '../../../spa/Components/Link/Link';

import {
    IPW_BASE_INPLAY_LINK,
    IPW_MAIN_CLASS_NAME,
    IPW_TITLE_ICON_CLASS_NAME,
    IPW_TITLE_LINK,
    IPW_FEATURED_IN_PLAY_TEXT,
    IPW_ALL_MARKETS_TEXT,
    IPW_BET_CARD_GTM_TAG,
    IPW_HOMEPAGE_NAME,
    IPW_INPLAY_NAME,
    IPW_GTM_SEE_ALL,
    IPW_INPLAY,
} from './constants';
import {
    HOME_COLUMN,
    DRAW_COLUMN,
    AWAY_COLUMN,
    TWO_WAY_COLUMN_LENGTH,
    THREE_WAY_COLUMN_LENGTH,
    FIRST_SELECTION_INDEX,
    SECOND_SELECTION_INDEX,
    THIRD_SELECTION_INDEX,
} from '../../../shared/components/selection-columns/constants';

import WidgetContainer from '../../../shared/ui/WidgetContainer';
import BetCard from '../../../shared/components/react/BetCard/index';
import { selectionArrayToObject } from '~shared/utils/selections-utils';

import '../styles/InplayWidget.scss';
import { seoUrlInplayEvent } from '../../../spa/Utils/SeoUrls';
import { getTeamLogoUrl } from '~shared/utils/sportsbook';

const ZERO_CONSTANT = 0;

export default class InplayWidget extends React.Component {

    static propTypes = {
        events: PropTypes.array,
        selections: PropTypes.object,
        isShowFooter: PropTypes.bool,
        markets_headers: PropTypes.object,
        baseInplayLink: PropTypes.string,
        mainClassName: PropTypes.string,
        titleIconClassName: PropTypes.string,
        source: PropTypes.string,
        titleLink: PropTypes.string,
    }

    static defaultProps = {
        events: null,
        selections: null,
        isShowFooter: false,
        markets_headers: {},
        baseInplayLink: IPW_BASE_INPLAY_LINK,
        mainClassName: IPW_MAIN_CLASS_NAME,
        titleIconClassName: IPW_TITLE_ICON_CLASS_NAME,
        titleLink: IPW_TITLE_LINK,
    }

    // @TODO to remove and don't set initial state
    state = {}

    // @TODO to remove and move refresh logic to container
    static getDerivedStateFromProps(props) {
        return props;
    }

    /**
     * Get the full URL suffixed by the eventId
     * @param {*} eventId eventId
     * @returns {String} full URL with eventId
     * @memberof InplayWidget
     */
    getUrl(eventId) {
        return seoUrlInplayEvent(`${this.props.baseInplayLink}/${eventId}`, eventId);
    }

    /**
     * Utility function to get classname of children without boilerplate code
     * to make it easier to generate BEM classnames
     * @param {String} className the part of the classname to suffix to the base class name
     * @returns {String} suffixed classname of the component i.e. component__subelement
     * @memberof InplayWidget
     */
    getClassName(className) {
        return `${this.props.mainClassName}${className}`;
    }

    getCompetitors(event) {
        const {
            competitors,
            sport_slug: sportSlug,
            category_slug: categorySlug,
            isUsFormat,
            tournament_logos: tournamentLogos,
        } = event;
        const competitorNames = competitors.map(({ name, slug }) => ({ name, slug }));
        if (isUsFormat && competitorNames.length) {
            competitorNames.reverse();
            competitorNames[0].name += ' @';
        }

        return competitorNames.map(({ name, slug: teamSlug }, key) => {
            let teamLogo = null;
            if (tournamentLogos && tournamentLogos.showTeamLogo) {
                const logoUrl = getTeamLogoUrl(sportSlug, categorySlug, teamSlug);
                teamLogo = <img src={logoUrl} />;
            }

            return (
                <div
                    key={ key }
                    className={ this.getClassName('__competitor') }
                >
                    {teamLogo}
                    { name }
                </div>
            );
        });
    }

    renderAllMarkets() {
        return (
            <div className={ this.getClassName('__all-markets-container') }>
                <span className={ this.getClassName('__all-markets') }>
                    <Trans>{IPW_ALL_MARKETS_TEXT}</Trans>
                </span>
                <span className={ this.getClassName('__all-markets-arrow') }></span>
            </div>
        );
    }

    renderCompetitorsAndMarkets(event) {
        return event.usesThreeMarketView
            ?
            (
                <div className={this.getClassName('__three_market_view')}>
                    { this.getCompetitors(event) }
                    { this.renderAllMarkets() }
                </div>
            )
            :
            (
                <>
                    <div className={this.getClassName('__c-competitor')}>
                        {this.getCompetitors(event)}
                    </div>
                    { this.renderAllMarkets() }
                </>
            );
    }

    _renderDescription(event) {
        const widgetName = (this.props.source === IPW_INPLAY) ? IPW_INPLAY_NAME : IPW_HOMEPAGE_NAME;
        const gtm = `${this.props.source};${widgetName};${IPW_BET_CARD_GTM_TAG}`;
        const threeMarketClassName = event.usesThreeMarketView ? `${this.getClassName('__group')}--three_market_view` : '';

        return (
            <Link
                to={ this.getUrl(event.id) }
                className={ this.getClassName('__link') }
                data-test-id={ `${this.getClassName('__link')}-${event.id}` }
                gtmAttribute={gtm}
            >
                <div className={ this.getClassName('__sport_symbol') }>
                    <div className={`bb-icon bb-icon--${event.sport_slug} bb-icon--size-18x18`}>
                    </div>
                </div>

                <div className={ `${this.getClassName('__group')} ${threeMarketClassName}` }>
                    { this.renderCompetitorsAndMarkets(event) }
                </div>
            </Link>
        );
    }

    /**
     * Reorder selections if number of selections is less than
     * number of market headers
     * @param {Array} selections array of selections
     * @param {Number} columnsLength number of market columns
     * @returns {Array} ordered array of selections
     * @memberof InplayWidget
     */
    reorderSelections(selections, columnsLength) {
        const numberOfSelections = selections.length;

        if (!numberOfSelections || numberOfSelections === columnsLength) {
            return selections;
        }

        const mappedSelections = columnsLength === TWO_WAY_COLUMN_LENGTH
            ? [null, null]
            : [null, null, null];

        if (numberOfSelections < columnsLength && columnsLength === THREE_WAY_COLUMN_LENGTH) {
            selections.forEach(selection => {
                const selectionType = selection.selection_type;
                if (selectionType === HOME_COLUMN) {
                    mappedSelections[FIRST_SELECTION_INDEX] = selection;
                } else if (selectionType === DRAW_COLUMN) {
                    mappedSelections[SECOND_SELECTION_INDEX] = selection;
                } else if (selectionType === AWAY_COLUMN) {
                    mappedSelections[THIRD_SELECTION_INDEX] = selection;
                }
            });
        }

        if (numberOfSelections < columnsLength && columnsLength === TWO_WAY_COLUMN_LENGTH) {
            const [selection] = selections;
            const selectionType = selection.selection_type;

            if (selectionType === HOME_COLUMN) {
                mappedSelections[FIRST_SELECTION_INDEX] = selection;
            } else {
                mappedSelections[SECOND_SELECTION_INDEX] = selection;
            }
        }

        return mappedSelections;
    }

    getAndUpdateSelections(selections, marketHeaders, columnsLength = ZERO_CONSTANT) {

        const calculatedColumnsLength = parseInt(columnsLength, 10) > ZERO_CONSTANT ? parseInt(columnsLength, 10) : Object.entries(marketHeaders).length;
        const sortedSelections = this.reorderSelections(selections, calculatedColumnsLength);

        return sortedSelections.map((item) => {
            if (!item) {
                return {
                    betable: false,
                };
            }

            const itemSuspended = item.suspended || (!item.active || !item.tradable);
            const itemIsBetable = itemSuspended ? !itemSuspended : item.betable;

            return {
                ...item,
                marketHeader: marketHeaders[item.selection_type],
                betable: itemIsBetable,
                suspended: itemSuspended,
            };
        });
    }

    getSelectionsWithMarket(event, selections, marketsHeaders) {
        const marketHeaders = marketsHeaders[event.market_id] || event.default_market_headers;

        return this.getAndUpdateSelections(selections, marketHeaders, event.sport_columns);
    }

    renderEvents(events, selections, markets, marketsHeaders) {
        const widgetName = (this.props.source === IPW_INPLAY) ? IPW_INPLAY_NAME : IPW_HOMEPAGE_NAME;
        const gtm = `${this.props.source};${widgetName};`;
        const betCards = events.map((event, key) => {
            const { id: eventId } = event;
            const eventSelections = selections[eventId] || [];
            const eventMarkets = markets[eventId] || {};
            const isBetable = !event.suspended && [].concat(...Object.values(eventSelections)).some(({suspended = true}) => !suspended);
            const { usesThreeMarketView, isUsFormat, grouped_market_ids: marketIds } = event;

            const selectionsBtns = usesThreeMarketView ? [] : this.getSelectionsWithMarket(event, eventSelections, marketsHeaders);
            let props = {
                description: this._renderDescription(event),
                betable: isBetable,
                selections: selectionsBtns,
                gtmAttribute: gtm,
                event,
                showScoreboard: true,
            };
            if (usesThreeMarketView) {
                const markets = {};
                // changing market format to match the preplay endpoint.
                Object.keys(eventSelections).forEach(marketId => {
                    const selections = eventSelections[marketId] || [];

                    markets[marketId] = {
                        ...eventMarkets[marketId],
                        selections: selectionArrayToObject(selections),
                    };
                });
                props = {
                    ...props,
                    markets,
                    marketOrder: marketIds,
                    usesThreeMarketView: true,
                    isUsFormat,
                };
            }

            return <BetCard key={ key } { ...props } />;
        });

        return (
            <React.Fragment>
                { betCards }
            </React.Fragment>
        );
    }

    render() {
        const { events, selections, markets, markets_headers } = this.state;

        if (!events || !selections) {
            return null;
        }

        const { isShowFooter, titleIconClassName } = this.props;
        const gtm = `${this.props.source};${IPW_HOMEPAGE_NAME};${IPW_GTM_SEE_ALL}`;

        const props = {
            title: <Trans>{IPW_FEATURED_IN_PLAY_TEXT}</Trans>,
            titleIconClassName,
            gtmAttribute: gtm,
        };

        if (isShowFooter) {
            props.titleLink = this.props.titleLink;
        }

        return (
            <div className={ IPW_MAIN_CLASS_NAME }>
                <WidgetContainer
                    { ...props }
                >
                    { this.renderEvents(events, selections, markets, markets_headers) }
                </WidgetContainer>
            </div>
        );
    }
}
